/** Angular imports */
import { computed, Injectable, signal } from '@angular/core';

/** Core imports */
import { ApiHostLocation } from '@core';

/** Local imports */
import { UiThumbnailContent } from '@features/media-library/interfaces';
import { PLAYLIST_STEPS } from '@features/playlists/constants';
import { CreatePlaylistDraft } from '@features/playlists/interfaces';

/**
 * This service is responsible for managing the steps of the create playlist flow
 */
@Injectable({
    providedIn: 'root',
})
export class CreatePlaylistStepsService {
    /** Holds all the steps for the create playlist flow */
    private steps = signal(PLAYLIST_STEPS);

    /** Holds the current create playlist step id */
    private _currentStepId = signal<number>(0);

    /** Public getter for the current step id */
    public currentStepId = computed(() => this._currentStepId());

    /**
     * Computed signal that checks if the current step is valid
     */
    public isStepValid = computed(() => {
        const stepId = this.currentStepId();
        const draft = this.playlistDraft();

        switch (stepId) {
            case 0: // fill-information
                return !!(
                    draft.fillInformation.playlistName &&
                    draft.fillInformation.playlistDescription &&
                    draft.fillInformation.assignToDealer
                );
            case 1: // select-assets
                return draft.selectAssets.addedPlaylistContents.length > 0;
            case 2: // settings
                return !!(draft.settings && draft.settings.duration >= 5);
            default:
                return true;
        }
    });

    /**
     * Holds the entire draft data for the create playlist flow
     */
    public playlistDraft = signal<CreatePlaylistDraft>({
        fillInformation: {
            playlistName: '',
            playlistDescription: '',
            assignToDealer: null,
        },
        selectAssets: {
            selectedContents: [],
            addedPlaylistContents: [],
        },
        selectHosts: [],
        settings: null,
    });

    /**
     * Updates a specific step in the playlist draft
     *
     * @param step The step key to update
     * @param data The new draft data for that step
     */
    public updateDraft(step: string, data: unknown): void {
        this.playlistDraft.update((current) => ({
            ...current,
            [step]: data,
        }));
    }

    /**
     * Checks if a specific step (by index) is valid based on draft data.
     *
     * @param stepId The zero-based step index to validate
     * @returns Whether the step's draft data satisfies its validation rules
     */
    public isStepValidById(stepId: number): boolean {
        const draft = this.playlistDraft();

        switch (stepId) {
            case 0: // fill-information
                return !!(
                    draft.fillInformation.playlistName &&
                    draft.fillInformation.playlistDescription &&
                    draft.fillInformation.assignToDealer
                );
            case 1: // select-assets
                return draft.selectAssets.addedPlaylistContents.length > 0;
            case 2: // settings
                return !!(draft.settings && draft.settings.duration >= 5);
            default:
                return true;
        }
    }

    /**
     * Navigates directly to a specific step by index.
     *
     * @param stepId - The zero-based step index to navigate to
     * @returns {void}
     */
    public goToStep(stepId: number): void {
        if (stepId >= 0 && stepId < this.steps().length) {
            this._currentStepId.set(stepId);
        }
    }

    /**
     * Moves to the next create playlist step
     * @returns {void}
     */
    public nextStep(): void {
        const currentStep = this.currentStepId();

        if (this.steps().length === currentStep + 1) {
            return;
        }

        this._currentStepId.set(currentStep + 1);
    }

    /**
     * Moves to the previous create playlist step
     * @returns {void}
     */
    public prevStep(): void {
        const currentStep = this.currentStepId();

        if (currentStep === 0) {
            return;
        }

        this._currentStepId.set(currentStep - 1);
    }
}
